import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Package,
  Zap,
  GitMerge,
  Cpu,
  Cable,
  LayoutGrid,
  Layers,
  Filter,
  Search,
  RefreshCw,
  Info,
} from "lucide-react";
import {
  solarManufacturerData,
  ASSET_TYPE_LABELS,
  ASSET_TYPE_COLORS,
  AssetType,
  ManufacturerEntry,
  getTotalStats,
} from "../data/solarManufacturerData";
import { manufacturerService } from "../../../services/manufacturerService";

const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  primaryBorder: "#D1E7DD",
  accent: "#A3CD39",
  dark: "#007F66",
  neutral: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#1F2937",
    900: "#111827",
  },
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

const ASSET_TYPE_ICONS: Record<AssetType, React.ReactNode> = {
  MOD: <Package className="w-4 h-4" />,
  INV: <Zap className="w-4 h-4" />,
  SCB: <GitMerge className="w-4 h-4" />,
  TRF: <Cpu className="w-4 h-4" />,
  CAB: <Cable className="w-4 h-4" />,
  MTB: <LayoutGrid className="w-4 h-4" />,
  SWG: <Layers className="w-4 h-4" />,
};

const ALL_ASSET_TYPES: AssetType[] = ["MOD", "INV", "SCB", "TRF", "CAB", "MTB", "SWG"];

interface ImportResult {
  manufacturers: { created: number; skipped: number; errors: string[] };
  models: { created: number; skipped: number; errors: string[] };
}

interface ManufacturerRowProps {
  manufacturer: ManufacturerEntry;
  selected: boolean;
  onToggle: () => void;
  filterType: AssetType | "ALL";
  search: string;
}

const ManufacturerRow: React.FC<ManufacturerRowProps> = ({
  manufacturer,
  selected,
  onToggle,
  filterType,
  search,
}) => {
  const [expanded, setExpanded] = useState(false);

  const visibleModels = useMemo(() => {
    let models = manufacturer.models;
    if (filterType !== "ALL") {
      models = models.filter((m) => m.assetType === filterType);
    }
    if (search) {
      const q = search.toLowerCase();
      models = models.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.description || "").toLowerCase().includes(q)
      );
    }
    return models;
  }, [manufacturer.models, filterType, search]);

  if (visibleModels.length === 0 && (filterType !== "ALL" || search)) return null;

  const assetTypesInMfg = [...new Set(manufacturer.models.map((m) => m.assetType))];

  return (
    <div
      className="border rounded-lg overflow-hidden transition-all duration-200"
      style={{
        borderColor: selected ? BRAND_COLORS.primaryBorder : BRAND_COLORS.neutral[200],
        background: selected ? BRAND_COLORS.primaryLight : "#fff",
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="w-4 h-4 rounded accent-green-600 cursor-pointer flex-shrink-0"
        />
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm">
              {manufacturer.name}
            </span>
            <div className="flex gap-1 flex-wrap">
              {assetTypesInMfg.map((type) => {
                const colors = ASSET_TYPE_COLORS[type];
                return (
                  <span
                    key={type}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
                    style={{
                      background: colors.bg,
                      color: colors.text,
                      borderColor: colors.border,
                    }}
                  >
                    {ASSET_TYPE_ICONS[type]}
                    {type}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {manufacturer.description}
          </div>
        </div>
        <div className="flex-shrink-0 text-xs text-gray-400 hidden sm:block">
          {visibleModels.length} model{visibleModels.length !== 1 ? "s" : ""}
          {manufacturer.website && (
            <a
              href={manufacturer.website}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 text-blue-500 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Website ↗
            </a>
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              className="border-t px-4 pb-3 pt-2"
              style={{ borderColor: BRAND_COLORS.neutral[100] }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {visibleModels.map((model, idx) => {
                  const colors = ASSET_TYPE_COLORS[model.assetType];
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-2 px-3 py-2 rounded-md border text-xs"
                      style={{
                        background: colors.bg,
                        borderColor: colors.border,
                      }}
                    >
                      <span
                        className="flex-shrink-0 mt-0.5"
                        style={{ color: colors.text }}
                      >
                        {ASSET_TYPE_ICONS[model.assetType]}
                      </span>
                      <div className="min-w-0">
                        <div
                          className="font-medium truncate"
                          style={{ color: colors.text }}
                        >
                          {model.name}
                        </div>
                        {model.description && (
                          <div className="text-gray-500 mt-0.5 leading-tight">
                            {model.description}
                          </div>
                        )}
                        {model.specifications && (
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {model.specifications.power !== undefined && (
                              <span className="text-gray-500">
                                {model.specifications.power}kW
                              </span>
                            )}
                            {model.specifications.efficiency !== undefined && (
                              <span className="text-gray-500">
                                η{model.specifications.efficiency}%
                              </span>
                            )}
                            {model.specifications.voltage !== undefined && (
                              <span className="text-gray-500">
                                {model.specifications.voltage}kV
                              </span>
                            )}
                            {model.specifications.capacity !== undefined && (
                              <span className="text-gray-500">
                                {model.specifications.capacity}kVA
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ImportManufacturersPage: React.FC = () => {
  const [selectedNames, setSelectedNames] = useState<Set<string>>(
    new Set(solarManufacturerData.map((m) => m.name))
  );
  const [filterType, setFilterType] = useState<AssetType | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const stats = useMemo(() => getTotalStats(), []);

  const filteredManufacturers = useMemo(() => {
    let list = solarManufacturerData;
    if (filterType !== "ALL") {
      list = list.filter((m) => m.models.some((model) => model.assetType === filterType));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.models.some(
            (model) =>
              model.name.toLowerCase().includes(q) ||
              (model.description || "").toLowerCase().includes(q)
          )
      );
    }
    return list;
  }, [filterType, search]);

  const allFilteredNames = filteredManufacturers.map((m) => m.name);
  const allFilteredSelected =
    allFilteredNames.length > 0 &&
    allFilteredNames.every((n) => selectedNames.has(n));
  const someFilteredSelected = allFilteredNames.some((n) => selectedNames.has(n));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedNames((prev) => {
        const next = new Set(prev);
        allFilteredNames.forEach((n) => next.delete(n));
        return next;
      });
    } else {
      setSelectedNames((prev) => {
        const next = new Set(prev);
        allFilteredNames.forEach((n) => next.add(n));
        return next;
      });
    }
  };

  const toggleManufacturer = (name: string) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectedCount = selectedNames.size;
  const selectedModels = solarManufacturerData
    .filter((m) => selectedNames.has(m.name))
    .reduce((sum, m) => sum + m.models.length, 0);

  const handleImport = async () => {
    if (selectedNames.size === 0) {
      toast.warning("Please select at least one manufacturer to import.");
      return;
    }

    const payload = {
      manufacturers: solarManufacturerData
        .filter((m) => selectedNames.has(m.name))
        .map((m) => ({
          name: m.name,
          description: m.description,
          website: m.website,
          contactInfo: m.contactInfo,
          models: m.models.map((model) => ({
            name: model.name,
            description: model.description,
            assetType: model.assetType,
            specifications: model.specifications,
          })),
        })),
    };

    setImporting(true);
    setResult(null);
    try {
      const response = await manufacturerService.bulkImport(payload);
      if (response.success) {
        setResult(response.data as ImportResult);
        const { manufacturers: mfg, models } = response.data as ImportResult;
        toast.success(
          `Import complete! ${mfg.created} manufacturers and ${models.created} models added.`
        );
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Stats Banner */}
      <div
        className="rounded-xl p-4 border"
        style={{
          background: `linear-gradient(135deg, ${BRAND_COLORS.primarySoft} 0%, #fff 100%)`,
          borderColor: BRAND_COLORS.primaryBorder,
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
          <span className="text-sm font-semibold" style={{ color: BRAND_COLORS.dark }}>
            Solar Asset Manufacturer Database
          </span>
          <span className="text-xs text-gray-500">
            — Curated list of globally recognised manufacturers for all solar asset types
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="bg-white rounded-lg px-3 py-2 border border-gray-100 col-span-2 sm:col-span-2">
            <div className="text-xl font-bold" style={{ color: BRAND_COLORS.primary }}>
              {stats.manufacturers}
            </div>
            <div className="text-xs text-gray-500">Total Manufacturers</div>
          </div>
          <div className="bg-white rounded-lg px-3 py-2 border border-gray-100 col-span-2 sm:col-span-2">
            <div className="text-xl font-bold text-gray-800">{stats.models}</div>
            <div className="text-xs text-gray-500">Total Models</div>
          </div>
          {ALL_ASSET_TYPES.map((type) => {
            const colors = ASSET_TYPE_COLORS[type];
            const typeStats = (stats.byAssetType as Record<string, { manufacturers: number; models: number }>)[type];
            return (
              <div
                key={type}
                className="rounded-lg px-3 py-2 border"
                style={{ background: colors.bg, borderColor: colors.border }}
              >
                <div className="flex items-center gap-1 mb-1">
                  <span style={{ color: colors.text }}>{ASSET_TYPE_ICONS[type]}</span>
                  <span className="text-xs font-bold" style={{ color: colors.text }}>
                    {type}
                  </span>
                </div>
                <div className="text-sm font-semibold" style={{ color: colors.text }}>
                  {typeStats?.models || 0}
                </div>
                <div className="text-xs" style={{ color: colors.text, opacity: 0.7 }}>
                  {typeStats?.manufacturers || 0} brands
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600 font-medium">Filter by type:</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterType("ALL")}
            className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
            style={
              filterType === "ALL"
                ? {
                    background: BRAND_COLORS.primary,
                    color: "#fff",
                    borderColor: BRAND_COLORS.primary,
                  }
                : {
                    background: "#fff",
                    color: BRAND_COLORS.neutral[600],
                    borderColor: BRAND_COLORS.neutral[200],
                  }
            }
          >
            All Types
          </button>
          {ALL_ASSET_TYPES.map((type) => {
            const colors = ASSET_TYPE_COLORS[type];
            const active = filterType === type;
            return (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                style={
                  active
                    ? { background: colors.text, color: "#fff", borderColor: colors.text }
                    : { background: colors.bg, color: colors.text, borderColor: colors.border }
                }
              >
                {ASSET_TYPE_ICONS[type]}
                {type} – {ASSET_TYPE_LABELS[type].split(" / ")[0]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search manufacturers or models..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ "--tw-ring-color": BRAND_COLORS.primary } as React.CSSProperties}
          />
        </div>

        {/* Select All / Import buttons */}
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={toggleAll}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-all"
            style={
              allFilteredSelected
                ? {
                    background: BRAND_COLORS.primarySoft,
                    color: BRAND_COLORS.dark,
                    borderColor: BRAND_COLORS.primaryBorder,
                  }
                : {
                    background: "#fff",
                    color: BRAND_COLORS.neutral[600],
                    borderColor: BRAND_COLORS.neutral[200],
                  }
            }
          >
            {allFilteredSelected ? (
              <>
                <XCircle className="w-4 h-4" />
                Deselect All
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Select All
                {someFilteredSelected && !allFilteredSelected && " Remaining"}
              </>
            )}
          </button>

          <button
            onClick={handleImport}
            disabled={importing || selectedCount === 0}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: importing ? BRAND_COLORS.neutral[400] : BRAND_COLORS.primary }}
          >
            {importing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Importing…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Import {selectedCount} Manufacturer{selectedCount !== 1 ? "s" : ""} ({selectedModels} models)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Import Result */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="rounded-xl border p-4"
            style={{
              background: BRAND_COLORS.successLight,
              borderColor: "#BBF7D0",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800">Import Completed Successfully</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="font-bold text-green-700">{result.manufacturers.created}</div>
                <div className="text-green-600">Manufacturers Created</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="font-bold text-gray-500">{result.manufacturers.skipped}</div>
                <div className="text-gray-400">Manufacturers Skipped</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="font-bold text-green-700">{result.models.created}</div>
                <div className="text-green-600">Models Created</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="font-bold text-gray-500">{result.models.skipped}</div>
                <div className="text-gray-400">Models Skipped</div>
              </div>
            </div>
            {(result.manufacturers.errors.length > 0 || result.models.errors.length > 0) && (
              <div
                className="mt-3 p-3 rounded-lg border"
                style={{ background: BRAND_COLORS.errorLight, borderColor: "#FECACA" }}
              >
                <div className="flex items-center gap-1.5 text-red-700 text-xs font-semibold mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Errors
                </div>
                {[...result.manufacturers.errors, ...result.models.errors].map((err, i) => (
                  <div key={i} className="text-xs text-red-600">
                    {err}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manufacturer List */}
      <div className="space-y-2">
        {filteredManufacturers.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <div>No manufacturers match your filter</div>
          </div>
        ) : (
          filteredManufacturers.map((mfg) => (
            <ManufacturerRow
              key={mfg.name}
              manufacturer={mfg}
              selected={selectedNames.has(mfg.name)}
              onToggle={() => toggleManufacturer(mfg.name)}
              filterType={filterType}
              search={search}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ImportManufacturersPage;
